"use client"

import * as React from "react"
import { CheckIcon } from "lucide-react"

import { cn } from "@ibada/ui"
import { moneyToNumberForChartGeometryOnly } from "@ibada/shared-types/finance"

import { useLocale, useTranslations } from "@/i18n"
import { CreatePreviewCard, CreatePreviewFacts } from "@/features/create-overlay/components/create-overlay-layout"
import { formatMoney } from "@/features/finance/lib/finance-utils"

/** Which way the money moves, for the colour and sign of the large amount. */
export enum FinancePreviewTone {
  /** Money in (an offering): teal, with a plus. */
  In = "in",
  /** Money out (an expense): red, with a minus. */
  Out = "out",
  /** Money moved inside the books (a transfer): neutral, no sign. */
  Neutral = "neutral",
}

const TONE_PILL: Record<FinancePreviewTone, string> = {
  [FinancePreviewTone.In]: "bg-teal-100 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300",
  [FinancePreviewTone.Out]: "bg-destructive/10 text-destructive",
  [FinancePreviewTone.Neutral]: "bg-muted text-foreground",
}

const TONE_AMOUNT: Record<FinancePreviewTone, string> = {
  [FinancePreviewTone.In]: "text-teal-700 dark:text-teal-300",
  [FinancePreviewTone.Out]: "text-destructive",
  [FinancePreviewTone.Neutral]: "text-foreground",
}

const TONE_SIGN: Record<FinancePreviewTone, string> = {
  [FinancePreviewTone.In]: "+",
  [FinancePreviewTone.Out]: "-",
  [FinancePreviewTone.Neutral]: "",
}

/** A long local date ("3 novembre 2026") from an ISO instant or a `yyyy-mm-dd` day; null when not a date. */
export function useFormatPreviewDate() {
  const locale = useLocale()
  return React.useCallback(
    (value: string | null | undefined) => {
      if (!value) return null
      // A bare day is read as that local day, never shifted by the timezone.
      const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value)
      if (Number.isNaN(date.getTime())) return null
      return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric" }).format(date)
    },
    [locale],
  )
}

/**
 * The first preview card of a finance create: the kind as a pill, the amount large (signed and coloured by
 * direction), then the key facts. An amount not typed yet shows a muted dash, never a zero.
 */
export function FinancePreviewAmountCard({
  kindLabel,
  tone,
  amount,
  currencyCode,
  facts,
  placeholder,
}: {
  kindLabel: string
  tone: FinancePreviewTone
  /** The canonical decimal string, or null while the field holds no amount. */
  amount: string | null
  currencyCode: string
  facts: { label: string; value: React.ReactNode }[]
  /** What stands in the amount's place while there is none (default: "Saisissez un montant"). */
  placeholder?: React.ReactNode
}) {
  const t = useTranslations("createOverlay.finance")
  return (
    <CreatePreviewCard data-testid='finance-preview-amount'>
      <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-extrabold", TONE_PILL[tone])}>{kindLabel}</span>
      {amount ? (
        <div
          className={cn(
            "mt-2.5 text-[28px] leading-tight font-extrabold tracking-[-0.5px] tabular-nums break-words",
            TONE_AMOUNT[tone],
          )}
        >
          {`${TONE_SIGN[tone]}${formatMoney(amount, currencyCode)}`}
        </div>
      ) : (
        // No amount yet: say so, quietly, rather than a lone dash under the pill.
        <div className='text-muted-foreground mt-2.5 text-base font-semibold'>{placeholder ?? t("enterAmount")}</div>
      )}
      <div className='mt-2'>
        <CreatePreviewFacts rows={facts} />
      </div>
    </CreatePreviewCard>
  )
}

/** A preview fact the form has not been given yet: a muted "Pas encore choisi" instead of a dash. */
export function NotChosen() {
  const t = useTranslations("createOverlay.finance")
  return <span className='text-muted-foreground font-medium'>{t("notChosen")}</span>
}

export type JournalPreviewLine = {
  key: string
  /** The account's code when the form knows it (income and expense accounts); financial accounts show a name only. */
  code?: string | null
  name: string
  debit?: string | null
  credit?: string | null
}

/**
 * The journal entries the save will post, as Débit / Crédit columns with "✓ Équilibré". The caller passes only
 * lines it derived the way the API builds them; it passes nothing (and this card is not drawn) while any side
 * is still unknown.
 */
export function JournalPreviewCard({ lines, currencyCode }: { lines: JournalPreviewLine[]; currencyCode: string }) {
  const t = useTranslations("createOverlay.finance")
  if (lines.length === 0) return null
  const figure = (value?: string | null) => (value ? formatMoney(value, currencyCode) : "")
  return (
    <CreatePreviewCard className='py-3' data-testid='finance-preview-journal'>
      <div className='text-muted-foreground grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2.5 pb-1 text-[11.5px] font-extrabold'>
        <span>{t("entries")}</span>
        <span className='min-w-12 text-right'>{t("debit")}</span>
        <span className='min-w-12 text-right'>{t("credit")}</span>
      </div>
      <div className='divide-border/50 divide-y'>
        {lines.map((line) => (
          <div key={line.key} className='grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-2.5 py-2 text-[12.5px]'>
            <span className='min-w-0 break-words'>
              {line.code ? <b className='text-muted-foreground mr-1 font-mono text-[11.5px]'>{line.code}</b> : null}
              {line.name}
            </span>
            <span className='min-w-12 text-right font-bold whitespace-nowrap tabular-nums'>{figure(line.debit)}</span>
            <span className='min-w-12 text-right font-bold whitespace-nowrap tabular-nums'>{figure(line.credit)}</span>
          </div>
        ))}
      </div>
      <div className='flex items-center gap-1.5 pt-2 text-xs font-bold text-teal-700 dark:text-teal-300'>
        <CheckIcon aria-hidden className='size-3.5' />
        {t("balanced")}
      </div>
    </CreatePreviewCard>
  )
}

export type FundPreviewRow = { key: string; label: string; amount: string }

/** Where the money belongs: one bar per fund, its width the fund's share of the total. */
export function FundSplitPreviewCard({
  rows,
  currencyCode,
  title,
}: {
  rows: FundPreviewRow[]
  currencyCode: string
  title?: string
}) {
  const t = useTranslations("createOverlay.finance")
  if (rows.length === 0) return null
  // Bar widths only: geometry, never money arithmetic.
  const total = rows.reduce((sum, row) => sum + moneyToNumberForChartGeometryOnly(row.amount), 0)
  return (
    <CreatePreviewCard className='flex flex-col gap-2 py-3' data-testid='finance-preview-funds'>
      <span className='text-muted-foreground text-[11.5px] font-extrabold'>{title ?? t("funds")}</span>
      {rows.map((row) => {
        const share = total > 0 ? Math.min(100, (moneyToNumberForChartGeometryOnly(row.amount) / total) * 100) : 0
        return (
          <div key={row.key} className='flex flex-col gap-1'>
            <div className='flex justify-between gap-3 text-[12.5px] font-bold'>
              <span className='min-w-0 truncate'>{row.label}</span>
              <span className='shrink-0 tabular-nums'>{formatMoney(row.amount, currencyCode)}</span>
            </div>
            <div className='bg-muted h-1.5 rounded-full'>
              <div className='bg-primary h-1.5 rounded-full' style={{ width: `${share}%` }} />
            </div>
          </div>
        )
      })}
    </CreatePreviewCard>
  )
}
